import React, { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { useRoomOfferInteraction } from 'hooks/roomOfferInteraction/useRoomOfferInteraction';
import { useIsRoomOfferAppLocked } from 'hooks/useIsRoomOfferAppLocked';
import { getIsLockedDealExcludingGHA } from 'modules/search/selectors';
import { getHotelApiRoomsParameters } from 'modules/searchApi/selectors';
import { Button } from '@daedalus/atlas/Button';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Icon } from '@daedalus/atlas/Icon';
import { trackProviderRedirect } from '@daedalus/core/src/analytics/modules/actions';
import { RoomContext, RoomOfferContext } from '@daedalus/core/src/analytics/types/Events';
import { BOFH_PROXY_PROVIDER_CODE } from '@daedalus/core/src/offer/business/isBofhOffer';
import { Offer } from '@daedalus/core/src/offer/types/offer';
import { Room } from '@daedalus/core/src/room/types/room';
import { useGetHotelQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { DealFreezeButton } from '@daedalus/shared/src/dealFreeze/components/DealFreezeButton';
import { ComponentSource } from '@daedalus/shared/src/dealFreeze/types';
type Props = {
  hotelId: string;
  room: Room;
  offer: Offer;
  component: string;
  roomContext: RoomContext;
  offerContext: RoomOfferContext;
  shouldShowDealFreezeButton?: boolean;
  isOfferSoldOut?: boolean;
};

// Keeps the button to the right when wrapped with long content
const ButtonLayout = /*#__PURE__*/styled('div')({
  name: "ButtonLayout",
  class: "bpv5ou4",
  propsAsIs: false
});
const ChevronWrapper = /*#__PURE__*/styled('div')({
  name: "ChevronWrapper",
  class: "cl1xwig",
  propsAsIs: false
});
const ctaMessages = defineMessages({
  unlock: {
    id: 'signUpCTAUnlock',
    defaultMessage: 'Unlock'
  },
  appLocked: {
    id: 'appBanner.button',
    defaultMessage: 'Get the app'
  },
  book: {
    id: 'book',
    defaultMessage: 'Book'
  },
  soldOut: {
    id: 'soldOut',
    defaultMessage: 'Sold out'
  }
});
type ButtonWrapperProps = PropsWithChildren<{
  shouldShowDealFreezeButton: boolean;
  isMobile: boolean;
}>;
const ButtonWrapper = ({
  children,
  shouldShowDealFreezeButton,
  isMobile
}: ButtonWrapperProps) => <ButtonLayout className={cx(shouldShowDealFreezeButton && '__showDealFreezeButton', isMobile && '__isMobile')}>
    {children}
  </ButtonLayout>;
const getIcon = ({
  isRoomOfferAppLocked,
  isLockedDeal
}: {
  isRoomOfferAppLocked: boolean;
  isLockedDeal: boolean;
}) => {
  if (isLockedDeal) return <Icon name="PdLocked" />;
  if (isRoomOfferAppLocked) return <Icon name="Iphone" />;
  return null;
};
export const RoomOfferCTA = ({
  hotelId,
  room,
  offer,
  component,
  roomContext,
  offerContext,
  shouldShowDealFreezeButton = false,
  isOfferSoldOut = false
}: Props) => {
  const isLockedDeal = useSelector(getIsLockedDealExcludingGHA(offer)) && !offer.isClicked;
  const {
    isRoomOfferAppLocked
  } = useIsRoomOfferAppLocked({
    offerId: offer.id
  });
  const {
    handleClick
  } = useRoomOfferInteraction({
    hotelId,
    roomId: room.id,
    offer,
    component,
    roomContext
  });
  const {
    data: hotel
  } = useGetHotelQuery({
    hotelId
  }, {
    skip: !hotelId
  });
  const {
    checkIn,
    checkOut,
    rooms
  } = useSelector(state => getHotelApiRoomsParameters(state, hotelId));
  const {
    isMobile
  } = useDeviceLayout();
  const showChevronIcon = !isRoomOfferAppLocked && !isLockedDeal;
  const ctaMessage = () => {
    if (isOfferSoldOut) return ctaMessages.soldOut;
    if (isRoomOfferAppLocked) return ctaMessages.appLocked;
    if (isLockedDeal) return ctaMessages.unlock;
    return ctaMessages.book;
  };
  return <ButtonWrapper shouldShowDealFreezeButton={shouldShowDealFreezeButton} isMobile={isMobile}>
      <Button onClick={handleClick} fullWidth variant="primary" iconStart={getIcon({
      isRoomOfferAppLocked,
      isLockedDeal
    })} iconEnd={showChevronIcon && <ChevronWrapper>
              <Icon name="ChevronRight" />
            </ChevronWrapper>} dataId={isRoomOfferAppLocked || isLockedDeal ? 'accommodationRoomLockedOfferCTA' : 'accommodationRoomOfferCTA'} disabled={isOfferSoldOut}>
        <FormattedMessage {...ctaMessage()} />
      </Button>
      {shouldShowDealFreezeButton && <DealFreezeButton fullWidth offerId={offer.id} hotel={hotel} searchDetail={{
      checkIn,
      checkOut,
      roomsSplit: rooms
    }} analyticsContext={{
      offerContext
    }} leadContextRequiredData={{
      trackProviderRedirect,
      providerRateType: offer.metadata.providerRateType,
      intermediaryProvider: BOFH_PROXY_PROVIDER_CODE
    }} room={room} component={ComponentSource.HotelViewRoomsSection} />}
    </ButtonWrapper>;
};

require("./RoomOfferCTA.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomOfferCTA.tsx");